/**
 * COMPTABILITÉ — LES NUITS.
 *
 * La liste de travail : ce qui reste à clôturer, ce qui est en cours, ce qui
 * est arrêté, et ce qui traîne un écart. Une table DENSE (`.fi-table`, ~38 px
 * de ligne) plutôt que la table aérée du design system : une comptable relit
 * trente nuits d'affilée, pas six.
 *
 * ═══ CE QUE LA LISTE NE FAIT PAS ═══
 * Elle n'invente aucune nuit. Une nuit sans clôture n'existe pas en base : la
 * nuit COURANTE est désignée par le serveur (`/night/current`), les nuits en
 * attente le sont aussi quand il les sert, et toute autre nuit est CHOISIE par
 * quelqu'un dans un calendrier. Fabriquer côté navigateur la liste des nuits
 * « qui auraient dû » être clôturées aurait demandé une cinquième convention
 * de nuit commerciale dans le dépôt — précisément le piège documenté par
 * l'architecture. C'est aussi pourquoi la borne « pas encore eu lieu » se lit
 * sur la nuit commerciale du serveur, jamais sur la date du navigateur.
 *
 * ═══ UNE NUIT OUBLIÉE SE RATTRAPE ═══
 * Un lundi férié, une semaine de congés : la nuit manquante ne manquait à
 * personne jusqu'au rapprochement bancaire, trois mois plus tard. Le geste
 * « ouvrir une autre nuit » existe donc depuis toutes les vues, l'historique
 * compris — et il commence par DEMANDER au serveur ce que cette nuit porte
 * déjà, pour reprendre un brouillon plutôt que d'en créer un second.
 *
 * ═══ LARGEUR ═══
 * À 390 px, c'est le CONTENEUR de la table qui défile (`.fi-tablewrap`), jamais
 * le document : une page qui glisse latéralement rend le reste de l'écran
 * inatteignable.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CalendarPlus, Coins, Plus, RotateCcw, X } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { DatePicker, SegmentedControl } from '../admin/pickers.js'
import { Modal, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import {
  CLOSING_STATUS_LABELS, CLOSING_STATUS_PILL, EnCours, Indisponible, Montant,
  ajouterJours, aujourdhuiParis, commeLignes, commeObjet, ilYA, lireJson,
  nuitCourte, nuitLongue,
  type ClosingRow, type CurrentNight, type Etat, type NuitOuvrable,
} from './ClosingCommon.js'
import { urlCloture } from './nights-common.js'
import { remplacerRecherche } from './adresse-ecran.js'
import { useMenuSoiree } from '../soirees/menu-soiree.js'

type Vue = 'a_clore' | 'en_cours' | 'historique' | 'ecarts'

const VUES: { value: Vue; label: string }[] = [
  { value: 'a_clore', label: 'À clôturer' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'historique', label: 'Historique' },
  { value: 'ecarts', label: 'Écarts' },
]

/** L'état demandé vit dans l'URL : un lien partagé rouvre la même vue. */
function vueDepuisUrl(): Vue {
  const v = new URLSearchParams(window.location.search).get('vue')
  return v === 'en_cours' || v === 'historique' || v === 'ecarts' ? v : 'a_clore'
}

function depuisDepuisUrl(defaut: string): string {
  const d = new URLSearchParams(window.location.search).get('depuis')
  return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : defaut
}

export function FinanceClosuresPage() {
  const toast = useToast()
  const { permissions } = usePermissions()
  const menuSoiree = useMenuSoiree()
  const peutOuvrir = permissions.includes('finance.closure.create')
  const peutReviser = permissions.includes('finance.closure.revise')

  const aujourdhui = useMemo(aujourdhuiParis, [])
  const [vue, setVue] = useState<Vue>(vueDepuisUrl)
  const [depuis, setDepuis] = useState<string>(() => depuisDepuisUrl(ajouterJours(aujourdhui, -90)))
  const [rows, setRows] = useState<Etat<ClosingRow[]>>({ etat: 'chargement' })
  const [nuit, setNuit] = useState<Etat<CurrentNight>>({ etat: 'chargement' })
  const [ouverture, setOuverture] = useState(false)

  /* ─── ouvrir une nuit PASSÉE : la date se choisit, elle ne se devine pas ─── */
  const [choix, setChoix] = useState<{ ouvert: boolean; date: string }>(
    () => ({ ouvert: false, date: ajouterJours(aujourdhui, -1) }),
  )
  const [surLaNuit, setSurLaNuit] = useState<Etat<ClosingRow[]>>({ etat: 'chargement' })
  const [enAttente, setEnAttente] = useState<Etat<NuitOuvrable[]>>({ etat: 'chargement' })

  const charger = useCallback(() => {
    setRows({ etat: 'chargement' })
    void lireJson<ClosingRow[]>(
      `/api/v1/finance/closings?from=${depuis}&to=${aujourdhui}`,
      commeLignes(['closings', 'items'], 'business_date'),
    ).then(setRows)
    void lireJson<CurrentNight>('/api/v1/finance/night/current', commeObjet(['night', 'current'], 'business_date')).then(setNuit)
  }, [depuis, aujourdhui])

  useEffect(charger, [charger])

  /* L'URL porte la vue et la borne : recharger la page ne perd pas le filtre,
     et un Retour depuis une clôture retrouve la même liste. L'entrée est
     REMPLACÉE (un filtre n'est pas une page) ; `adresse-ecran.ts` dit
     pourquoi ce n'est pas `redirect`, qui empêchait l'arrivée de remonter
     en haut de la liste. */
  useEffect(() => {
    const p = new URLSearchParams()
    if (vue !== 'a_clore') p.set('vue', vue)
    p.set('depuis', depuis)
    remplacerRecherche('/comptabilite/clotures', p)
  }, [vue, depuis])

  const ouvrir = async (businessDate: string) => {
    setOuverture(true)
    const r = await adminMutate<{ error?: string; id?: string; message?: string }>(
      'POST', '/api/v1/finance/closings', { business_date: businessDate },
    )
    setOuverture(false)
    if (r.status >= 200 && r.status < 300 && typeof r.data.id === 'string') {
      setChoix((c) => ({ ...c, ouvert: false }))
      navigate(urlCloture(businessDate))
      return
    }
    toast('error', r.data.message ?? 'La clôture n’a pas pu être ouverte.')
  }

  /* Ce que la nuit choisie porte DÉJÀ. On ne propose jamais « ouvrir » sans
     l'avoir demandé au serveur : deux brouillons pour la même nuit, ce sont
     deux vérités, et c'est exactement ce que le domaine refuse. */
  useEffect(() => {
    if (!choix.ouvert) return
    setSurLaNuit({ etat: 'chargement' })
    void lireJson<ClosingRow[]>(
      `/api/v1/finance/closings?from=${choix.date}&to=${choix.date}`,
      commeLignes(['closings', 'items'], 'business_date'),
    ).then(setSurLaNuit)
  }, [choix])

  /* Les nuits qui attendent encore quelque chose. Lecture d'appoint : si le
     serveur ne la sert pas, le choix de date suffit — et l'écran le dit au
     lieu de faire semblant d'avoir une liste. */
  useEffect(() => {
    if (!choix.ouvert) return
    void lireJson<NuitOuvrable[]>(
      '/api/v1/finance/nights/openable',
      commeLignes(['nights', 'openable', 'items'], 'business_date'),
    ).then(setEnAttente)
  }, [choix.ouvert])

  const toutes = rows.etat === 'ok' ? rows.valeur : []
  const visibles = toutes.filter((c) => {
    if (vue === 'en_cours') return c.status === 'DRAFT' || c.status === 'READY'
    if (vue === 'historique') return c.status === 'CLOSED' || c.status === 'SUPERSEDED'
    if (vue === 'ecarts') return c.open_discrepancies > 0
    return c.status === 'DRAFT' || c.status === 'READY'
  })

  const nuitCourante = nuit.etat === 'ok' ? nuit.valeur : null
  const nuitSansCloture = nuitCourante !== null && nuitCourante.closing === null

  /* La borne haute est la NUIT COMMERCIALE du serveur, pas la date civile du
     navigateur : à 3 h du matin, les deux ne désignent pas la même nuit, et
     recalculer la coupure de 8 h ici aurait fait une cinquième convention. */
  const plafond = nuitCourante?.business_date ?? aujourdhui
  const nuitAVenir = choix.date > plafond
  const cloturesDeLaNuit = surLaNuit.etat === 'ok' ? surLaNuit.valeur : []
  const brouillon = cloturesDeLaNuit.find((c) => c.status === 'DRAFT' || c.status === 'READY') ?? null
  const finalisee = cloturesDeLaNuit.find((c) => c.status === 'CLOSED') ?? null
  const nuitsSansCloture = enAttente.etat === 'ok'
    ? enAttente.valeur.filter((n) => n.closing === null).slice(0, 8)
    : []

  return (
    <main className="lc-page lc-page--large lc-stagger fh-page">
      <SmartBack />
      <header className="fh-hero">
        <span className="fh-hero__icon" aria-hidden="true"><Coins size={26} /></span>
        <div>
          <h1 className="lc-title">Clôtures &amp; caisses</h1>
          <p className="lc-subtitle">
            Une ligne par nuit commerciale. Une clôture finalisée ne se modifie
            plus : on la corrige en créant une révision, qui laisse la
            précédente intacte.
          </p>
        </div>
      </header>

      {/* ═══ La nuit courante, quand elle n'a pas encore de clôture ═══ */}
      {vue === 'a_clore' && nuitSansCloture && nuitCourante ? (
        <section className="lc-panel fic-encours">
          <div className="lc-panel__body fic-encours__body">
            <div>
              <p className="fi-kpi__label">Nuit commerciale en cours</p>
              <p className="fic-encours__date">{nuitLongue(nuitCourante.business_date)}</p>
              <p className="lc-hint">
                {nuitCourante.soirees.length === 0
                  ? 'Aucune soirée déclarée.'
                  : nuitCourante.soirees.map((s) => s.name).join(' · ')}
                {' · '}
                {nuitCourante.sources.length} document{nuitCourante.sources.length > 1 ? 's' : ''} reçu{nuitCourante.sources.length > 1 ? 's' : ''}
              </p>
            </div>
            {peutOuvrir ? (
              <button className="lc-btn lc-btn--primary" disabled={ouverture}
                onClick={() => void ouvrir(nuitCourante.business_date)}>
                <Plus size={17} aria-hidden="true" />{ouverture ? 'Ouverture en cours' : 'Ouvrir la clôture'}
              </button>
            ) : (
              <span className="lc-hint">Votre rôle ne permet pas d’ouvrir une clôture.</span>
            )}
          </div>
        </section>
      ) : null}

      {/* ═══ Filtres ═══ */}
      <section className="lc-panel">
        <div className="lc-panel__head fic-filtres">
          <SegmentedControl<Vue> value={vue} options={VUES} onChange={setVue} ariaLabel="Filtrer les clôtures" />
          <span className="lc-panel__spacer" />
          <DatePicker
            value={depuis}
            onPick={setDepuis}
            label={`Depuis le ${nuitCourte(depuis)}`}
            ariaLabel="Choisir la première nuit affichée"
          />
          {/* Une nuit oubliée ne se rattrape que si on peut la DÉSIGNER. Le
              geste est offert depuis toutes les vues, l'historique compris :
              c'est là qu'on s'aperçoit qu'un mardi manque. */}
          <button className="lc-btn" onClick={() => setChoix((c) => ({ ...c, ouvert: true }))}>
            <CalendarPlus size={16} aria-hidden="true" />Ouvrir une autre nuit
          </button>
          <button className="lc-btn lc-btn--ghost lc-btn--icon" onClick={charger} aria-label="Recharger la liste">
            <RotateCcw size={16} />
          </button>
        </div>

        <div className="lc-panel__body lc-panel__body--flush">
          {rows.etat === 'chargement' ? <EnCours quoi="des clôtures" /> : null}
          {rows.etat === 'absent' ? (
            <Indisponible titre="Clôtures indisponibles" message={rows.message}>
              <button className="lc-btn lc-btn--sm fih-retry" onClick={charger}>Réessayer</button>
            </Indisponible>
          ) : null}

          {rows.etat === 'ok' && visibles.length === 0 ? (
            <div className="fi-empty">
              <div className="fi-empty__title">
                {vue === 'ecarts' ? 'Aucun écart ouvert' : vue === 'historique' ? 'Aucune clôture arrêtée' : 'Rien en attente'}
              </div>
              <p>
                {vue === 'ecarts'
                  ? 'Toutes les différences relevées depuis cette date ont été expliquées.'
                  : vue === 'historique'
                    ? 'Aucune nuit n’a encore été finalisée sur cette période.'
                    : 'Aucune clôture n’est ouverte ni prête à finaliser sur cette période.'}
              </p>
            </div>
          ) : null}

          {visibles.length > 0 ? (
            <div className="fi-tablewrap fic-wrap">
              <table className="fi-table">
                <thead>
                  <tr>
                    <th scope="col">Nuit</th>
                    <th scope="col">État</th>
                    <th scope="col">Révision</th>
                    <th scope="col">Écarts ouverts</th>
                    <th scope="col" className="fi-money">Retenu</th>
                    <th scope="col">Finalisée</th>
                    <th scope="col"><span className="fic-sr">Ouvrir</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((c) => (
                    <tr key={c.id}>
                      <th scope="row" className="fic-nuit">{nuitCourte(c.business_date)}</th>
                      <td>
                        <span className={CLOSING_STATUS_PILL[c.status]}>{CLOSING_STATUS_LABELS[c.status]}</span>
                      </td>
                      <td>{c.revision > 1 ? `Révision ${c.revision}` : 'Première'}</td>
                      <td>
                        {c.open_discrepancies > 0 ? (
                          <span className="fic-ecarts">
                            <AlertTriangle size={14} aria-hidden="true" />
                            {c.open_discrepancies}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="fi-money">
                        {c.status === 'CLOSED'
                          ? <Montant cents={c.retained_cents} fort />
                          : <span className="fi-money fi-money--faible" title="Le retenu naît à la finalisation.">—</span>}
                      </td>
                      <td>
                        {c.closed_at
                          ? <span className="fic-close">{ilYA(c.closed_at)}{c.closed_by_name ? ` · ${c.closed_by_name}` : ''}</span>
                          : '—'}
                      </td>
                      <td className="fic-cta">
                        <Link className="lc-btn lc-btn--ghost lc-btn--sm" href={c.status === 'SUPERSEDED' ? `/comptabilite/clotures/${c.id}` : urlCloture(c.business_date)}
                          {...menuSoiree.nuit({ date: c.business_date, cloture: { statut: c.status, href: c.status === 'SUPERSEDED' ? `/comptabilite/clotures/${c.id}` : undefined } })}>
                          {c.status === 'CLOSED' || c.status === 'SUPERSEDED' ? 'Consulter' : 'Reprendre'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </section>

      <p className="lc-hint fic-note">
        Le « retenu » ne s’affiche qu’une fois la nuit finalisée : avant, aucun
        montant n’est arrêté, et en montrer un donnerait à un brouillon
        l’apparence d’un chiffre officiel.
      </p>

      {/* ═══════════ ouvrir la clôture d'une autre nuit ═══════════
          Aucune ACTION CRITIQUE n'est lancée d'ici : réviser une nuit
          finalisée exige une ré-authentification forte et se fait sur la
          nuit elle-même, où le motif se saisit. Cette surface DÉSIGNE une
          nuit et y conduit — elle ne décide rien à sa place. */}
      <Modal
        open={choix.ouvert}
        onClose={() => setChoix((c) => ({ ...c, ouvert: false }))}
        ariaLabel="Ouvrir la clôture d’une autre nuit"
        width={620}
        owning
      >
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true"><CalendarPlus size={22} /></span>
          <div>
            <div className="lc-modal__title">Ouvrir une autre nuit</div>
            <div className="lc-modal__pills">
              <span className="lc-pill lc-pill--idle">{nuitLongue(choix.date)}</span>
            </div>
          </div>
          <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close"
            onClick={() => setChoix((c) => ({ ...c, ouvert: false }))} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        <div className="lc-modal__body fic-modal">
          <div className="lc-field">
            <span className="lc-label">Nuit commerciale</span>
            <DatePicker
              value={choix.date}
              onPick={(ymd) => setChoix({ ouvert: true, date: ymd })}
              label={`Nuit du ${nuitCourte(choix.date)}`}
              ariaLabel="Choisir la nuit à clôturer"
            />
            <span className="lc-helper">
              Une nuit commerciale porte la date de son SOIR : celle du 1ᵉʳ août
              court jusqu’au matin du 2.
            </span>
          </div>

          {nuitsSansCloture.length > 0 ? (
            <div className="fic-attente">
              <p className="fi-kpi__label">Nuits sans clôture</p>
              <ul>
                {nuitsSansCloture.map((n) => (
                  <li key={n.business_date}>
                    <button className="lc-btn lc-btn--ghost lc-btn--sm"
                      onClick={() => setChoix({ ouvert: true, date: n.business_date })}>
                      {nuitCourte(n.business_date)}
                    </button>
                    {/* La route est écrite en parallèle : on lit ce qui est
                        là, on n'invente pas ce qui manque. */}
                    <span className="lc-hint">
                      {Array.isArray(n.soirees) && n.soirees.length > 0
                        ? n.soirees.map((s) => s.name).join(' · ')
                        : 'aucune soirée déclarée'}
                      {typeof n.source_count === 'number'
                        ? ` · ${n.source_count} document${n.source_count > 1 ? 's' : ''}`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {enAttente.etat === 'absent' ? (
            <p className="lc-hint">
              La liste des nuits en attente n’est pas encore servie par le
              serveur : choisissez la date ci-dessus.
            </p>
          ) : null}

          {nuitAVenir ? (
            <p className="fi-provisoire">
              Cette nuit n’a pas encore eu lieu : il n’y a rien à y clôturer.
              La dernière nuit clôturable est celle du {nuitCourte(plafond)}.
            </p>
          ) : (
            <>
              {surLaNuit.etat === 'chargement' ? <EnCours quoi="de cette nuit" /> : null}
              {surLaNuit.etat === 'absent' ? (
                <Indisponible titre="Cette nuit n’a pas pu être vérifiée" message={surLaNuit.message} />
              ) : null}

              {surLaNuit.etat === 'ok' && brouillon ? (
                <div className="fic-choix">
                  <p>
                    Une clôture est déjà ouverte sur cette nuit{' '}
                    <span className={CLOSING_STATUS_PILL[brouillon.status]}>
                      {CLOSING_STATUS_LABELS[brouillon.status]}
                    </span>. On la reprend : en ouvrir une seconde produirait
                    deux vérités pour la même nuit.
                  </p>
                  <button className="lc-btn lc-btn--primary" onClick={() => {
                    setChoix((c) => ({ ...c, ouvert: false }))
                    navigate(urlCloture(brouillon.business_date))
                  }}>Reprendre le brouillon</button>
                </div>
              ) : null}

              {surLaNuit.etat === 'ok' && !brouillon && finalisee ? (
                <div className="fic-choix">
                  <p>
                    Cette nuit est finalisée{finalisee.closed_at ? ` ${ilYA(finalisee.closed_at)}` : ''}
                    {finalisee.closed_by_name ? ` par ${finalisee.closed_by_name}` : ''}. Elle ne se
                    rouvre pas : on la consulte, ou on la corrige par une révision qui laisse
                    celle-ci intacte et consultable.
                  </p>
                  <div className="fic-choix__actions">
                    <button className="lc-btn" onClick={() => {
                      setChoix((c) => ({ ...c, ouvert: false }))
                      navigate(urlCloture(finalisee.business_date))
                    }}>Consulter la nuit</button>
                    {peutReviser ? (
                      <button className="lc-btn lc-btn--primary" onClick={() => {
                        setChoix((c) => ({ ...c, ouvert: false }))
                        navigate(`${urlCloture(finalisee.business_date)}/synthese`)
                      }}>Corriger : créer une révision</button>
                    ) : (
                      <span className="lc-hint">Votre rôle ne permet pas de créer une révision.</span>
                    )}
                  </div>
                </div>
              ) : null}

              {surLaNuit.etat === 'ok' && !brouillon && !finalisee ? (
                <div className="fic-choix">
                  <p>
                    {cloturesDeLaNuit.length === 0
                      ? 'Aucune clôture n’existe sur cette nuit.'
                      : 'Seules des révisions remplacées subsistent sur cette nuit : la nouvelle clôture repartira du numéro suivant.'}
                  </p>
                  {peutOuvrir ? (
                    <button className="lc-btn lc-btn--primary" disabled={ouverture}
                      onClick={() => void ouvrir(choix.date)}>
                      <Plus size={16} aria-hidden="true" />
                      {ouverture ? 'Ouverture en cours' : 'Ouvrir la clôture de cette nuit'}
                    </button>
                  ) : (
                    <span className="lc-hint">Votre rôle ne permet pas d’ouvrir une clôture.</span>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </Modal>
    </main>
  )
}
